import { useState, useCallback } from 'react';
import { Dialog } from '@/components/common/Dialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';
import { formatTime } from '@/lib/format';
import type { Translations } from '@/i18n/types';

// ── Types ───────────────────────────────────────────────

interface SetupData {
  chassisId?: string;
  engineId?: string;
  tyreId?: string;
  carbId?: string;
  axleId?: string;
  sparkPlugId?: string;
  flOut?: string; frOut?: string; rlOut?: string; rrOut?: string;
  flIn?: string; frIn?: string; rlIn?: string; rrIn?: string;
  rearWidth?: string;
  frontHubId?: string;
  rearHubId?: string;
  loosenedRearBumper?: boolean | string;
  frontCaster?: string;
  frontCamber?: string;
  frontTrackWidth?: string;
  frontRideHeight?: string;
  frontAckermann?: string;
  frontToe?: string;
  frontTorsionBar?: string;
  frontSprocket?: string;
  rearSprocket?: string;
  carbMainJet?: string;
  carbIdleJet?: string;
  carbNeedleClip?: string;
  carbAirScrew?: string;
  carbFloatHeight?: string;
  weight?: string;
  notes?: string;
}

interface InventoryItem {
  id?: string;
  brand?: string;
  type?: string;
  [key: string]: unknown;
}

interface Inventory {
  chassis: InventoryItem[];
  engines: InventoryItem[];
  tyres: InventoryItem[];
  carburetors: InventoryItem[];
  axles: InventoryItem[];
  hubs: InventoryItem[];
  sparkPlugs: InventoryItem[];
}

interface Candidate {
  sessionId: string;
  date: string;
  time: string;
  trackName: string;
  driverName: string;
  completedLaps: number;
  bestLapTimeMs: number;
  [key: string]: unknown;
}

// ── Field label mapping ─────────────────────────────────

const FIELD_LABEL_KEYS: Record<string, keyof Translations> = {
  chassisId: 'propagation.field.chassis',
  weight: 'propagation.field.weight',
  engineId: 'propagation.field.engine',
  tyreId: 'propagation.field.tyre',
  carbId: 'propagation.field.carburetor',
  axleId: 'propagation.field.axle',
  flOut: 'propagation.field.flOut',
  frOut: 'propagation.field.frOut',
  rlOut: 'propagation.field.rlOut',
  rrOut: 'propagation.field.rrOut',
  flIn: 'propagation.field.flIn',
  frIn: 'propagation.field.frIn',
  rlIn: 'propagation.field.rlIn',
  rrIn: 'propagation.field.rrIn',
  rearWidth: 'propagation.field.rearWidth',
  frontHubId: 'propagation.field.frontHubs',
  rearHubId: 'propagation.field.rearHubs',
  loosenedRearBumper: 'propagation.field.rearBumper',
  frontCaster: 'propagation.field.caster',
  frontCamber: 'propagation.field.camber',
  frontTrackWidth: 'propagation.field.trackWidth',
  frontRideHeight: 'propagation.field.rideHeight',
  frontAckermann: 'propagation.field.ackermann',
  frontToe: 'propagation.field.toe',
  frontTorsionBar: 'propagation.field.torsionBar',
  frontSprocket: 'propagation.field.frontSprocket',
  rearSprocket: 'propagation.field.rearSprocket',
  carbMainJet: 'propagation.field.mainJet',
  carbIdleJet: 'propagation.field.idleJet',
  carbNeedleClip: 'propagation.field.needleClip',
  carbAirScrew: 'propagation.field.airScrew',
  carbFloatHeight: 'propagation.field.floatHeight',
  notes: 'propagation.field.notes',
  sparkPlugId: 'propagation.field.sparkPlug',
};

const FK_FIELDS = new Set([
  'chassisId', 'engineId', 'tyreId', 'carbId', 'axleId',
  'frontHubId', 'rearHubId', 'sparkPlugId',
]);

// ── Props ───────────────────────────────────────────────

interface Props {
  candidates: Candidate[];
  matchFields: Record<string, string>;
  newSetup: SetupData;
  inventory: Inventory;
  onClose: () => void;
}

// ── Component ───────────────────────────────────────────

export function PropagationDialog({ candidates, matchFields, newSetup, inventory, onClose }: Props) {
  const { showToast } = useToast();
  const { t } = useI18n();
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(candidates.map((_, i) => i)),
  );
  const [applying, setApplying] = useState(false);

  const resolveName = useCallback((field: string, val: string): string => {
    if (field === 'loosenedRearBumper') return val === 'true' ? t('common.yes') : t('common.no');
    if (!FK_FIELDS.has(field)) return val || '—';
    if (!val) return '—';
    const maps: Record<string, InventoryItem[]> = {
      chassisId: inventory.chassis ?? [],
      engineId: inventory.engines ?? [],
      tyreId: inventory.tyres ?? [],
      carbId: inventory.carburetors ?? [],
      axleId: inventory.axles ?? [],
      frontHubId: inventory.hubs ?? [],
      rearHubId: inventory.hubs ?? [],
      sparkPlugId: inventory.sparkPlugs ?? [],
    };
    const item = (maps[field] ?? []).find(x => x.id === val);
    return item ? `${item.brand ?? ''} ${item.type ?? ''}`.trim() : val;
  }, [inventory, t]);

  const toggleCheck = (idx: number) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAll = useCallback(() => {
    if (checked.size === candidates.length) {
      setChecked(new Set());
    } else {
      setChecked(new Set(candidates.map((_, i) => i)));
    }
  }, [checked.size, candidates]);

  const selectedCandidates = candidates.filter((_, i) => checked.has(i));
  const allChecked = checked.size === candidates.length;

  const handleApply = async () => {
    if (checked.size === 0) {
      showToast(t('propagation.selectAtLeastOne'), 'error');
      return;
    }
    setApplying(true);
    const sessionIds = selectedCandidates.map(c => c.sessionId);
    try {
      await rpc<{ updated: number }>('BulkApplySessionSetup', {
        sessionIds,
        ...(newSetup as unknown as Record<string, unknown>),
      });
      showToast(t('propagation.applied'));
      onClose();
    } catch (e) {
      showToast((e as Error).message, 'error');
      setApplying(false);
    }
  };

  const changesEntries = Object.entries(matchFields);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('propagation.title')}
      subtitle={t('propagation.subtitle')}
      footer={
        <>
          <button
            onClick={handleApply}
            disabled={applying || checked.size === 0}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-accent text-white disabled:opacity-50 cursor-pointer transition-colors"
          >
            {applying ? t('common.applying') : `${t('propagation.apply')} (${checked.size})`}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary
              border border-border-default hover:border-border-hover hover:text-text-primary transition-colors cursor-pointer"
          >
            {t('common.skip')}
          </button>
        </>
      }
    >
      {/* Changes summary */}
      <div className="mb-5">
        <div className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted mb-2">
          {t('propagation.changes')}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {changesEntries.map(([field, oldVal]) => (
            <span
              key={field}
              className="inline-flex items-center gap-1 text-[0.72rem] bg-white/[0.04] rounded-md px-2 py-1"
            >
              <span className="text-text-muted">{FIELD_LABEL_KEYS[field] ? t(FIELD_LABEL_KEYS[field]) : field}</span>
              <span className="text-text-muted opacity-60 line-through">{resolveName(field, oldVal)}</span>
              <span className="text-text-muted">→</span>
              <span className="text-text-primary font-medium">
                {resolveName(field, String((newSetup as Record<string, unknown>)[field] ?? ''))}
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* Session checklist */}
      <div>
        <div className="text-[0.68rem] font-semibold uppercase tracking-wider text-text-muted mb-2">
          {t('propagation.sessions')}
        </div>

        {/* Select all */}
        <div
          className="flex items-center gap-2.5 py-1.5 cursor-pointer hover:bg-white/[0.03] transition-colors"
          onClick={toggleAll}
        >
          <input
            type="checkbox"
            checked={allChecked}
            onChange={toggleAll}
            className="w-3.5 h-3.5 accent-accent cursor-pointer"
            onClick={e => e.stopPropagation()}
          />
          <span className="text-[0.72rem] text-text-muted">
            {allChecked ? t('propagation.deselectAll') : t('propagation.selectAll')}
          </span>
          <span className="text-[0.68rem] text-text-muted ml-auto">{checked.size}/{candidates.length}</span>
        </div>

        {/* List */}
        <div className="max-h-[260px] overflow-y-auto -mx-1">
          {candidates.map((c, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-1 py-1.5 rounded cursor-pointer hover:bg-white/[0.03] transition-colors"
              onClick={() => toggleCheck(i)}
            >
              <input
                type="checkbox"
                checked={checked.has(i)}
                onChange={() => toggleCheck(i)}
                className="w-3.5 h-3.5 accent-accent cursor-pointer shrink-0"
                onClick={e => e.stopPropagation()}
              />
              <span className="text-[0.72rem] tabular-nums text-text-muted min-w-[68px]">{c.date}</span>
              <span className="text-[0.72rem] tabular-nums text-text-muted min-w-[36px]">{(c.time || '').slice(0, 5)}</span>
              <span className="text-[0.72rem] text-text-primary font-medium flex-1 truncate">{c.trackName}</span>
              <span className="text-[0.68rem] text-text-muted tabular-nums">{c.completedLaps ?? 0}L</span>
              <span className="text-[0.68rem] text-accent tabular-nums min-w-[52px] text-right">{formatTime(c.bestLapTimeMs || 0)}</span>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
